import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtUSD, fmtECU, fmtRateUsdPerEcu } from "../lib/format";
import { Copy, Check, Trash2, Plus } from "lucide-react";
import {
  PageHeader, Panel, DataTable, DsButton, AgentPortrait, StatusLight, Chip, Meter,
} from "../ds";
import { DeleteAgentDialog } from "../components/DeleteAgentDialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";

function CreateKeyDialog({ open, onOpenChange, agents, onCreated }) {
  const [name, setName] = useState("");
  const [allowed, setAllowed] = useState([]);
  const [scopes, setScopes] = useState(["agents:*"]);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleAgent = (n) => {
    setAllowed((v) => v.includes(n) ? v.filter((x) => x !== n) : [...v, n]);
  };
  const submit = async () => {
    if (!name.trim()) return toast.error("Please add a name.");
    if (scopes.length === 0) return toast.error("Pick at least one scope.");
    setBusy(true);
    try {
      const { data } = await api.post("/settings/api-keys",
        { name, allowed_agents: allowed, scopes });
      setNewKey(data);
      onCreated && onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const close = () => {
    setName(""); setAllowed([]); setScopes(["agents:*"]);
    setNewKey(null); setCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="create-key-dialog" className="bg-[color:var(--panel)] border-[color:var(--line)]">
        <DialogHeader>
          <DialogTitle>Create API key</DialogTitle>
          <DialogDescription>Keys are shown once. Store it in your agent service's secret manager.</DialogDescription>
        </DialogHeader>
        {!newKey ? (
          <>
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. dev-agent-prod"
                  className="bg-[color:var(--panel-2)] border-[color:var(--line-2)]"
                  data-testid="ck-name"
                />
              </div>
              <div>
                <Label>Allowed agents (empty = any)</Label>
                <div className="mt-2 space-y-1.5 max-h-48 overflow-auto border border-[color:var(--line-2)] rounded-[5px] p-3 bg-[color:var(--panel-2)]">
                  {agents.map((a) => (
                    <label key={a.name} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allowed.includes(a.name)}
                        onChange={() => toggleAgent(a.name)}
                        data-testid={`ck-agent-${a.name}`}
                      />
                      <span>{a.name}</span>
                      <span className="text-[10px] font-mono text-[color:var(--muted)]">· {a.department}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Scopes</Label>
                <div className="mt-2 space-y-1.5 border border-[color:var(--line-2)] rounded-[5px] p-3 bg-[color:var(--panel-2)]">
                  {[
                    { v: "agents:*",    hint: "all agent operations" },
                    { v: "costs:write", hint: "post cost deltas" },
                    { v: "backup:read", hint: "download nightly backup (exclusive)" },
                  ].map(({v, hint}) => {
                    const backupOnly = scopes.includes("backup:read");
                    const disabled = (v === "backup:read" ? scopes.some(s => s!=="backup:read") :
                                      (backupOnly && v !== "backup:read"));
                    return (
                      <label key={v} className={`flex items-center gap-2 text-sm cursor-pointer ${disabled ? "opacity-40" : ""}`}>
                        <input
                          type="checkbox"
                          checked={scopes.includes(v)}
                          disabled={disabled}
                          onChange={() => setScopes((s) =>
                            s.includes(v) ? s.filter(x=>x!==v) : [...s, v])}
                          data-testid={`ck-scope-${v.replace(":","-").replace("*","star")}`}
                        />
                        <span className="font-mono">{v}</span>
                        <span className="text-[10px] text-[color:var(--muted)]">· {hint}</span>
                      </label>
                    );
                  })}
                  <p className="font-mono text-[10px] text-[color:var(--muted)] mt-2">
                    backup:read keys cannot carry any other scope.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <DsButton variant="ghost" onClick={close}>Cancel</DsButton>
              <DsButton variant="primary" onClick={submit} disabled={busy} data-testid="ck-submit">Create</DsButton>
            </DialogFooter>
          </>
        ) : (
          <>
            <div>
              <p className="text-sm text-[color:var(--muted)] mb-2">
                Here is your new API key for <span className="font-semibold text-[color:var(--fg)]">{newKey.name}</span>. Copy it now — you will not see it again.
              </p>
              <div className="flex items-center gap-2 border border-[color:var(--line-2)] rounded-[5px] p-3 bg-[color:var(--panel-2)]">
                <code className="font-mono text-xs break-all flex-1 text-[color:var(--fg)]" data-testid="ck-plaintext">{newKey.key}</code>
                <DsButton
                  onClick={() => { navigator.clipboard.writeText(newKey.key); setCopied(true); }}
                  data-testid="ck-copy"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </DsButton>
              </div>
            </div>
            <DialogFooter>
              <DsButton variant="primary" onClick={close} data-testid="ck-close">Done</DsButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function statusKey(a) {
  if (a.status === "blocked") return "blocked";
  if (!a.enabled) return "offline";
  return "working";
}

export default function Settings() {
  const qc = useQueryClient();
  const { user, changePassword } = useAuth();

  const { data: keysData } = useQuery({
    queryKey: ["api-keys"],
    queryFn: async () => (await api.get("/settings/api-keys")).data,
  });
  const { data: agentsData } = useQuery({
    queryKey: ["agents-registry"],
    queryFn: async () => (await api.get("/settings/agents")).data,
  });
  const { data: billingData } = useQuery({
    queryKey: ["billing"],
    queryFn: async () => (await api.get("/settings/billing")).data,
    refetchInterval: 60000,
  });

  const keys = keysData?.keys || [];
  const agents = agentsData?.agents || [];
  const purchases = billingData?.purchases || [];

  const [openCreate, setOpenCreate] = useState(false);
  const [openAgent, setOpenAgent] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [purchase, setPurchase] = useState({ date: "", usd: "", ecu: "", note: "" });
  const [budgetUsd, setBudgetUsd] = useState("");
  const [fallbackRate, setFallbackRate] = useState("");

  useEffect(() => {
    if (billingData) {
      setBudgetUsd(String(billingData.daily_agent_budget_usd ?? ""));
      setFallbackRate(String(billingData.fallback_usd_per_ecu ?? ""));
    }
  }, [billingData]);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? Agents using it will stop working.")) return;
    try {
      await api.delete(`/settings/api-keys/${id}`);
      toast.success("API key revoked. It is now hidden from this list.");
      qc.invalidateQueries({ queryKey: ["api-keys"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleAgent = async (a) => {
    try { await api.patch(`/settings/agents/${a.id}`, { enabled: !a.enabled });
      qc.invalidateQueries({ queryKey: ["agents-registry"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteDemo = async () => {
    if (!window.confirm("Delete all demo tasks and their events?")) return;
    try {
      const { data } = await api.delete("/settings/demo");
      toast.success(`Removed ${data.tasks_removed} tasks, ${data.events_removed} events.`);
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["board"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [cur, setCur] = useState(""); const [np, setNp] = useState("");
  const [np2, setNp2] = useState(""); const [perr, setPerr] = useState("");
  const [pbusy, setPbusy] = useState(false);
  const submitPwd = async (e) => {
    e.preventDefault(); setPerr("");
    if (np.length < 10) return setPerr("New password must be at least 10 chars.");
    if (np !== np2) return setPerr("Passwords do not match.");
    setPbusy(true);
    try { await changePassword(cur, np); toast.success("Password updated."); setCur(""); setNp(""); setNp2(""); }
    catch (e) { setPerr(formatApiError(e)); }
    finally { setPbusy(false); }
  };

  const addPurchase = async () => {
    if (!purchase.date || !purchase.usd || !purchase.ecu) return toast.error("Date, USD, and ECU are required.");
    try {
      await api.post("/settings/billing/purchases", {
        date: purchase.date, usd: Number(purchase.usd),
        ecu: Number(purchase.ecu), note: purchase.note,
      });
      toast.success("Purchase recorded.");
      setPurchase({ date: "", usd: "", ecu: "", note: "" });
      qc.invalidateQueries({ queryKey: ["billing"] });
      qc.invalidateQueries({ queryKey: ["agent-usd-rate"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deletePurchase = async (pid) => {
    if (!window.confirm("Delete this credit purchase?")) return;
    try { await api.delete(`/settings/billing/purchases/${pid}`); toast.success("Deleted.");
      qc.invalidateQueries({ queryKey: ["billing"] });
      qc.invalidateQueries({ queryKey: ["agent-usd-rate"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveBillingSettings = async () => {
    try {
      await api.patch("/settings/billing", {
        daily_agent_budget_usd: Number(budgetUsd),
        fallback_usd_per_ecu: Number(fallbackRate),
      });
      toast.success("Billing settings updated.");
      qc.invalidateQueries({ queryKey: ["billing"] });
      qc.invalidateQueries({ queryKey: ["agent-usd-rate"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openAgentForm = (a) => { setEditingAgent(a); setOpenAgent(true); };
  const closeAgentForm = () => { setEditingAgent(null); setOpenAgent(false); };
  const [agentToDelete, setAgentToDelete] = useState(null);
  const saveAgent = async (payload) => {
    try {
      if (editingAgent?.id) {
        await api.patch(`/settings/agents/${editingAgent.id}`, payload);
      } else {
        await api.post("/settings/agents", payload);
      }
      toast.success("Agent saved.");
      qc.invalidateQueries({ queryKey: ["agents-registry"] });
      closeAgentForm();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-10 max-w-[1600px] mx-auto space-y-8">
      <PageHeader title="Settings" subtitle="Agents, keys, billing, your password" />

      {/* AGENTS */}
      <Panel
        label="AGENTS"
        action={
          <DsButton variant="primary" onClick={() => openAgentForm(null)} data-testid="add-agent-btn">
            <Plus className="h-4 w-4" /> Add agent
          </DsButton>
        }
        testId="settings-agents-panel"
      >
        <DataTable
          testId="agents-table"
          columns={[
            { key: "name", label: "Agent", width: 240,
              render: (a) => (
                <div className="flex items-center gap-3">
                  <AgentPortrait agent={a} size={34} />
                  <div className="min-w-0">
                    <div className="font-semibold text-[color:var(--fg)]">{a.name}</div>
                    <div className="text-xs text-[color:var(--muted)] truncate">{a.description || "—"}</div>
                  </div>
                </div>
              ) },
            { key: "department", label: "Department", width: 180,
              render: (a) => (
                <span className="font-mono text-xs uppercase text-[color:var(--muted)]">{a.department}</span>
              ) },
            { key: "status", label: "Status", width: 150,
              render: (a) => <StatusLight status={a.status || "idle"} /> },
            { key: "level", label: "Level", width: 90,
              render: (a) => <Chip value={a.approval_level?.toLowerCase() || "normal"} label={a.approval_level || "L0"} /> },
            { key: "spend", label: "Monthly spend / limit", width: 260,
              render: (a) => {
                const used = a.monthly_spent_usd || 0;
                const cap = a.monthly_budget_usd || 0;
                return (
                  <div className="min-w-[220px]">
                    <div className="flex items-baseline justify-between font-mono text-xs tabular-nums">
                      <span className="text-[color:var(--fg)]">{fmtUSD(used)}</span>
                      <span className="text-[color:var(--muted)]">/ {cap > 0 ? fmtUSD(cap) : "no cap"}</span>
                    </div>
                    {cap > 0 && <Meter value={used} max={cap} testId={`agent-meter-${a.name}`} />}
                  </div>
                );
              } },
            { key: "actions", label: "", width: 230, right: true,
              render: (a) => (
                <div className="inline-flex items-center gap-2 justify-end">
                  <Switch
                    checked={!!a.enabled}
                    onCheckedChange={() => toggleAgent(a)}
                    data-testid={`agent-toggle-${a.name}`}
                  />
                  <DsButton onClick={() => openAgentForm(a)} data-testid={`agent-edit-${a.name}`}>
                    Edit
                  </DsButton>
                  {a.is_system ? (
                    <TooltipProvider delayDuration={120}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex" data-testid={`agent-delete-${a.name}-wrap`}>
                            <DsButton
                              disabled
                              aria-disabled="true"
                              data-testid={`agent-delete-${a.name}`}
                              variant="no"
                            >
                              Sil
                            </DsButton>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          Çekirdek ajan — korumalı, silinemez.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <DsButton
                      onClick={() => setAgentToDelete(a)}
                      title="Ajanı sil"
                      data-testid={`agent-delete-${a.name}`}
                      variant="no"
                    >
                      Sil
                    </DsButton>
                  )}
                </div>
              ) },
          ]}
          rows={agents}
          empty="No agents yet."
        />
      </Panel>

      {/* API KEYS */}
      <Panel
        label="API KEYS"
        action={
          <DsButton variant="primary" onClick={() => setOpenCreate(true)} data-testid="create-key-btn">
            <Plus className="h-4 w-4" /> Create key
          </DsButton>
        }
        testId="settings-keys-panel"
      >
        <DataTable
          testId="keys-table"
          columns={[
            { key: "name", label: "Name" },
            { key: "key_prefix", label: "Prefix", mono: true, width: 160 },
            { key: "allowed_agents", label: "Agents",
              render: (k) => k.allowed_agents?.length ? k.allowed_agents.join(", ") : "any" },
            { key: "created_at", label: "Created", mono: true, width: 160,
              render: (k) => fmtDate(k.created_at) },
            { key: "actions", label: "", right: true, width: 110,
              render: (k) => (
                <DsButton onClick={() => revoke(k.id)} data-testid={`revoke-${k.id}`}>Revoke</DsButton>
              ) },
          ]}
          rows={keys}
          empty="No active API keys. Create one above."
        />
      </Panel>

      {/* BILLING */}
      <Panel label="BILLING" testId="settings-billing-panel">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-[7px] border border-[color:var(--line)] bg-[color:var(--panel-2)] p-4" data-testid="billing-rate">
            <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)]">
              Current USD / ECU
            </p>
            <p className="font-mono text-2xl mt-2 text-[color:var(--fg)] tabular-nums">
              {fmtRateUsdPerEcu(billingData?.usd_per_ecu)}
            </p>
            <p className="text-xs text-[color:var(--muted)] mt-1">
              Weighted average of all credit purchases, or the fallback rate if none exist.
            </p>
            <div className="mt-4 space-y-2">
              <div>
                <Label>Fallback USD / ECU</Label>
                <Input
                  value={fallbackRate} onChange={(e) => setFallbackRate(e.target.value)}
                  type="number" step="0.0001" data-testid="fallback-rate"
                  className="bg-[color:var(--panel)] border-[color:var(--line-2)]"
                />
              </div>
              <div>
                <Label>Daily agent budget (USD)</Label>
                <Input
                  value={budgetUsd} onChange={(e) => setBudgetUsd(e.target.value)}
                  type="number" step="0.1" data-testid="daily-budget"
                  className="bg-[color:var(--panel)] border-[color:var(--line-2)]"
                />
              </div>
              <DsButton variant="primary" onClick={saveBillingSettings} data-testid="save-billing">
                Save
              </DsButton>
            </div>
          </div>

          <div className="rounded-[7px] border border-[color:var(--line)] bg-[color:var(--panel-2)] p-4" data-testid="billing-add">
            <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)]">
              Add credit purchase
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div><Label>Date</Label>
                <Input type="date" value={purchase.date}
                  onChange={(e) => setPurchase({...purchase, date: e.target.value})}
                  className="bg-[color:var(--panel)] border-[color:var(--line-2)]"
                  data-testid="purchase-date" />
              </div>
              <div><Label>Note</Label>
                <Input value={purchase.note}
                  onChange={(e) => setPurchase({...purchase, note: e.target.value})}
                  className="bg-[color:var(--panel)] border-[color:var(--line-2)]"
                  data-testid="purchase-note" />
              </div>
              <div><Label>USD paid</Label>
                <Input type="number" step="0.01" value={purchase.usd}
                  onChange={(e) => setPurchase({...purchase, usd: e.target.value})}
                  className="bg-[color:var(--panel)] border-[color:var(--line-2)]"
                  data-testid="purchase-usd" />
              </div>
              <div><Label>ECU received</Label>
                <Input type="number" step="0.01" value={purchase.ecu}
                  onChange={(e) => setPurchase({...purchase, ecu: e.target.value})}
                  className="bg-[color:var(--panel)] border-[color:var(--line-2)]"
                  data-testid="purchase-ecu" />
              </div>
            </div>
            <DsButton variant="primary" className="mt-3" onClick={addPurchase} data-testid="purchase-add">
              <Plus className="h-4 w-4" /> Add
            </DsButton>
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between pb-3">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)]">
              CREDIT PURCHASES
            </span>
            <span className="font-mono text-[10.5px] text-[color:var(--muted)]" data-testid="settings-rate-pill">
              RATE ${billingData?.usd_per_ecu != null ? Number(billingData.usd_per_ecu).toFixed(4) : "—"} / ECU
            </span>
          </div>
          <DataTable
            testId="purchases-table"
            columns={[
              { key: "date", label: "Date", mono: true, width: 140,
                render: (p) => p.date },
              { key: "usd", label: "USD", right: true, width: 140,
                render: (p) => fmtUSD(p.usd) },
              { key: "ecu", label: "ECU", right: true, width: 140,
                render: (p) => fmtECU(p.ecu) },
              { key: "note", label: "Note",
                render: (p) => p.note || "—" },
              { key: "actions", label: "", right: true, width: 80,
                render: (p) => (
                  <DsButton onClick={() => deletePurchase(p.id)} data-testid={`purchase-del-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </DsButton>
                ) },
            ]}
            rows={purchases}
            empty="No purchases recorded yet."
          />
        </div>
      </Panel>

      {/* PASSWORD */}
      <Panel label="CHANGE PASSWORD" testId="settings-pwd-panel">
        <form onSubmit={submitPwd} className="space-y-3 max-w-md" data-testid="pwd-form">
          <div><Label>Current password</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)}
              className="bg-[color:var(--panel-2)] border-[color:var(--line-2)]"
              data-testid="pwd-current" />
          </div>
          <div><Label>New password</Label>
            <Input type="password" value={np} onChange={(e) => setNp(e.target.value)}
              className="bg-[color:var(--panel-2)] border-[color:var(--line-2)]"
              data-testid="pwd-new" />
          </div>
          <div><Label>Confirm new password</Label>
            <Input type="password" value={np2} onChange={(e) => setNp2(e.target.value)}
              className="bg-[color:var(--panel-2)] border-[color:var(--line-2)]"
              data-testid="pwd-confirm" />
          </div>
          {perr && <div className="text-sm" style={{ color: "var(--bad)" }}>{perr}</div>}
          <DsButton variant="primary" type="submit" disabled={pbusy} data-testid="pwd-submit">
            {pbusy ? "Saving…" : "Update password"}
          </DsButton>
        </form>
      </Panel>

      {/* DEMO */}
      <Panel label="DEMO DATA" testId="settings-demo-panel">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-[color:var(--muted)]">
            Delete all tasks created by the seed script.
          </p>
          <DsButton variant="no" onClick={deleteDemo} data-testid="delete-demo">
            Delete demo data
          </DsButton>
        </div>
      </Panel>

      <CreateKeyDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        agents={agents}
        onCreated={() => qc.invalidateQueries({ queryKey: ["api-keys"] })}
      />
      <AgentFormDialog
        open={openAgent}
        onOpenChange={(v) => (v ? setOpenAgent(true) : closeAgentForm())}        agent={editingAgent}
        onSave={saveAgent}
      />
      <DeleteAgentDialog
        open={!!agentToDelete}
        onOpenChange={(v) => { if (!v) setAgentToDelete(null); }}
        agent={agentToDelete}
      />
    </div>
  );
}

function AgentFormDialog({ open, onOpenChange, agent, onSave }) {
  const [form, setForm] = useState({
    name: "", department: "Product Studio", role: "specialist",
    model: "claude-sonnet-4-5", description: "", approval_level: "L0",
    monthly_budget_usd: 0, monthly_budget_ecu: 0, status: "active",
    tools: "", skills: "", parent_agent: "",
  });

  useEffect(() => {
    if (agent) {
      setForm({
        name: agent.name || "", department: agent.department || "Product Studio",
        role: agent.role || "specialist", model: agent.model || "claude-sonnet-4-5",
        description: agent.description || "", approval_level: agent.approval_level || "L0",
        monthly_budget_usd: agent.monthly_budget_usd || 0,
        monthly_budget_ecu: agent.monthly_budget_ecu || 0,
        status: agent.status || "active",
        tools: (agent.tools || []).join(", "),
        skills: (agent.skills || []).join(", "),
        parent_agent: agent.parent_agent || "",
      });
    } else {
      setForm({
        name: "", department: "Product Studio", role: "specialist",
        model: "claude-sonnet-4-5", description: "", approval_level: "L0",
        monthly_budget_usd: 0, monthly_budget_ecu: 0, status: "active",
        tools: "", skills: "", parent_agent: "",
      });
    }
  }, [agent, open]);

  const submit = () => {
    const payload = {
      ...form,
      monthly_budget_usd: Number(form.monthly_budget_usd || 0),
      monthly_budget_ecu: Number(form.monthly_budget_ecu || 0),
      tools: form.tools.split(",").map((t) => t.trim()).filter(Boolean),
      skills: form.skills.split(",").map((t) => t.trim()).filter(Boolean),
      parent_agent: form.parent_agent || null,
    };
    if (agent?.id) delete payload.name;
    onSave(payload);
  };

  const inputCls = "bg-[color:var(--panel-2)] border-[color:var(--line-2)]";
  const selectCls = "h-10 w-full rounded-[5px] border border-[color:var(--line-2)] bg-[color:var(--panel-2)] px-3 text-sm text-[color:var(--fg)]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="agent-form-dialog" className="max-w-xl max-h-[85vh] overflow-y-auto bg-[color:var(--panel)] border-[color:var(--line)]">
        <DialogHeader>
          <DialogTitle>{agent ? `Edit ${agent.name}` : "Add agent"}</DialogTitle>
          <DialogDescription>Agent metadata used by the agent service and reports.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Name {agent && <span className="text-[10px] font-mono text-[color:var(--muted)]">(not editable)</span>}</Label>
            <Input value={form.name} disabled={!!agent}
              onChange={(e) => setForm({...form, name: e.target.value})}
              className={inputCls} data-testid="agent-name" />
          </div>
          <div><Label>Department</Label>
            <Input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className={inputCls} data-testid="agent-dept" />
          </div>
          <div><Label>Role</Label>
            <select className={selectCls} value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} data-testid="agent-role">
              <option value="specialist">specialist</option>
              <option value="orchestrator">orchestrator</option>
            </select>
          </div>
          <div><Label>Model</Label>
            <Input value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} className={inputCls} data-testid="agent-model" />
          </div>
          <div><Label>Approval level</Label>
            <select className={selectCls} value={form.approval_level} onChange={(e) => setForm({...form, approval_level: e.target.value})} data-testid="agent-approval">
              <option value="L0">L0</option><option value="L1">L1</option><option value="L2">L2</option>
            </select>
          </div>
          <div><Label>Status</Label>
            <select className={selectCls} value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} data-testid="agent-status">
              <option value="active">active</option><option value="mode">mode</option>
              <option value="planned">planned</option><option value="disabled">disabled</option>
            </select>
          </div>
          <div><Label>Parent agent</Label>
            <Input value={form.parent_agent} onChange={(e) => setForm({...form, parent_agent: e.target.value})} className={inputCls} data-testid="agent-parent" />
          </div>
          <div><Label>Monthly budget (USD)</Label>
            <Input type="number" value={form.monthly_budget_usd}
              onChange={(e) => setForm({...form, monthly_budget_usd: e.target.value})}
              className={inputCls} data-testid="agent-budget-usd" />
          </div>
          <div><Label>Monthly budget (ECU)</Label>
            <Input type="number" value={form.monthly_budget_ecu}
              onChange={(e) => setForm({...form, monthly_budget_ecu: e.target.value})}
              className={inputCls} data-testid="agent-budget-ecu" />
          </div>
          <div className="col-span-2"><Label>Tools (comma separated)</Label>
            <Input value={form.tools} onChange={(e) => setForm({...form, tools: e.target.value})} className={inputCls} data-testid="agent-tools" />
          </div>
          <div className="col-span-2"><Label>Skills (comma separated)</Label>
            <Input value={form.skills} onChange={(e) => setForm({...form, skills: e.target.value})} className={inputCls} data-testid="agent-skills" />
          </div>
          <div className="col-span-2"><Label>Description</Label>
            <textarea className="w-full min-h-20 rounded-[5px] border border-[color:var(--line-2)] bg-[color:var(--panel-2)] px-3 py-2 text-sm text-[color:var(--fg)]"
              value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="agent-desc" />
          </div>
        </div>
        <DialogFooter>
          <DsButton variant="ghost" onClick={() => onOpenChange(false)}>Cancel</DsButton>
          <DsButton variant="primary" onClick={submit} data-testid="agent-save">Save</DsButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
